import { useCallback, useRef, useState } from "react";

export type SSEStatus = "idle" | "streaming" | "completed" | "error";

interface StreamProgress {
  progress: number;
  total?: number;
  code: string;
  message: string;
}

interface StreamError {
  message: string;
  code: string;
}

interface UseSSEOptions<
  TStart = Record<string, unknown>,
  TEnd = Record<string, unknown>,
  TState = Record<string, unknown>,
> {
  url: string;
  method?: "GET" | "POST";
  headers?: Record<string, string>;
  body?: unknown;
  onStart?: (data: TStart) => void;
  onProgress?: (data: StreamProgress) => void;
  onState?: (data: TState) => void;
  onEnd?: (data: TEnd) => void;
  onError?: (data: StreamError) => void;
  onDebug?: (data: Record<string, unknown>) => void;
}

function parseSSEBlock(
  block: string,
): { event?: string; data?: unknown } | null {
  const lines = block
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n")
    .split("\n");

  let event: string | undefined;
  const dataLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith(":")) continue;

    const idx = trimmed.indexOf(":");
    const field = idx === -1 ? trimmed : trimmed.slice(0, idx);
    const value = idx === -1 ? "" : trimmed.slice(idx + 1).trimStart();

    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }

  if (dataLines.length === 0 && !event) return null;

  const raw = dataLines.join("\n");
  let data: unknown;
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      data = raw;
    }
  }

  return { event, data };
}

export function useSSE<
  TStart = Record<string, unknown>,
  TEnd = Record<string, unknown>,
  TState = Record<string, unknown>,
>() {
  const [status, setStatus] = useState<SSEStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const isStreaming = status === "streaming";

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus((s) => (s === "idle" ? "idle" : "completed"));
  }, []);

  const reset = useCallback(() => {
    stop();
    setError(null);
    setStatus("idle");
  }, [stop]);

  const start = useCallback(
    async (options: UseSSEOptions<TStart, TEnd, TState>) => {
      stop();
      setError(null);
      setStatus("streaming");

      const abort = new AbortController();
      abortRef.current = abort;

      try {
        const fetchOptions: RequestInit = {
          method: options.method ?? "POST",
          credentials: "include",
          headers: {
            Accept: "text/event-stream",
            ...(options.method !== "GET"
              ? { "Content-Type": "application/json" }
              : {}),
            ...(options.headers ?? {}),
          },
          signal: abort.signal,
        };

        if (options.method !== "GET" && options.body !== undefined) {
          fetchOptions.body = JSON.stringify(options.body);
        }

        const res = await fetch(options.url, fetchOptions);

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(
            `HTTP ${res.status} ${res.statusText}${text ? ` - ${text}` : ""}`,
          );
        }

        if (!res.body) {
          throw new Error("Response has no body (stream unsupported).");
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder
            .decode(value, { stream: true })
            .replaceAll("\r\n", "\n");

          while (true) {
            const sep = buffer.indexOf("\n\n");
            if (sep === -1) break;

            const block = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);

            const parsed = parseSSEBlock(block);
            if (!parsed) continue;

            const eventName = parsed.event ?? "message";
            const eventData = parsed.data;

            switch (eventName) {
              case "stream_start":
                options.onStart?.(eventData as TStart);
                break;
              case "stream_progress":
                options.onProgress?.(eventData as StreamProgress);
                break;
              case "stream_state":
                options.onState?.(eventData as TState);
                break;
              case "stream_end":
                options.onEnd?.(eventData as TEnd);
                setStatus("completed");
                abortRef.current?.abort();
                abortRef.current = null;
                return;
              case "stream_error": {
                const errData = eventData as StreamError;
                options.onError?.(errData);
                setError(errData?.message ?? "Erro desconhecido");
                setStatus("error");
                abortRef.current?.abort();
                abortRef.current = null;
                return;
              }
              case "stream_debug":
                options.onDebug?.(eventData as Record<string, unknown>);
                break;
            }
          }
        }

        setStatus("completed");
      } catch (e) {
        if (abort.signal.aborted) return;
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        setStatus("error");
      } finally {
        if (abortRef.current === abort) {
          abortRef.current = null;
        }
      }
    },
    [stop],
  );

  return { start, stop, reset, isStreaming, status, error };
}
